<template>
    <div v-if="from && until">
        <template v-for="(months, year) in daysByYearMonthWeek">
        <div v-for="(weeks, month) in months" :key="`${year}-${month}`">
            <h2>{{ $t(`month-long-${Number(month)+1}`) }} {{ year }}</h2>
            <button
                class="btn btn-sm btn-outline-dark bg-white position-fixed current-week"
                style="right: 20px; top: 10px; z-index: 10"
                @click="scrollToWeek"
            >
                {{ $t('go-to-current-week') }}
            </button>

            <table class="table table-bordered timetable-calendar">
            <tbody>
            <template v-for="(week, weekNo) in weeks">
                <tr :key="`${year}-${month}-${weekNo}-1`" :id="`${year}-${month}-${weekNo}-1`">
                    <!-- Cannot colspan these; CSS calcs are based on rows of 7 elements -->
                    <th v-for="n in week.offsetStart" :key="n" class="hide" />
                    <th scope="col" class="timetable-calendar-heading"  v-for="(day, date) in week.days" :key="`${date}-1`">
                        {{ `${$t('dayname-short-' + (day.day === 0 ? 7 : day.day))}` }} {{ date }}
                    </th>
                    <th v-for="n in week.offsetEnd" :key="n" class="hide" />
                </tr>

                <tr :key="`${year}-${month}-${weekNo}-2`" class="mb-2">
                    <td v-for="n in week.offsetStart" :key="n" class="hide" />
                    <td v-for="(day, date) in week.days" :key="`${date}-2`" :class="[0, 6].includes(day.day) ? 'bg-secondary' : 'bg-white'">
                        <div class="timetable-period">
                            <TimetableEvent
                                v-for="event in day.events"
                                :key="event.id"
                                :event="event"
                                :hideDetailsOnSmallScreen="true"
                                @click="$emit('selectEvent', event)"
                                class="timetable-event"
                            />
                        </div>
                    </td>
                    <td v-for="n in week.offsetEnd" :key="n" class="hide" />
                </tr>
                <tr :key="`${year}-${month}-${weekNo}-3`">
                    <td colspan="7" class="p-2 hide" />
                </tr>
            </template>
            </tbody>
            </table>
        </div>
        </template>
    </div>
</template>

<script>
import EventContainer from 'src/modules/timetable/eventContainer'
import { dateFromISODate } from 'src/modules/timetable/util'

import TimetableEvent from './TimetableEvent.vue'

export default {
    components: {
        TimetableEvent,
    },
    props: {
        userId: {
            type: Number,
            required: true,
        },
        from: String,
        until: String,
        events: {
            type: Array,
            default: () => [],
        },
        bgEvents: {
            type: Array,
            default: () => [],
        },
    },
    mounted() {
        this.eventContainer = new EventContainer(this.events, this.bgEvents.map(event => {
            delete event.groups
            return event
        }))
        this.updateDays()
    },
    data() {
        return {
            eventContainer: null,
            daysByYearMonthWeek: {},
            startDate: null,
            endDate: null,
        }
    },
    methods: {
        /**
         * Generates the structure to generate the timetable from as a year -> month -> day -> details dictionary
         */
        updateDays() {
            if (!this.from || !this.until) return

            this.daysByYearMonthWeek = {}

            const start = dateFromISODate(this.from)
            const end = dateFromISODate(this.until)

            let prev = new Date(start)
            let week = this.getWeek(start) - (start.getDay() === 1 ? 1 : 0)

            for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
                if (date.getMonth() !== prev.getMonth()) {
                    this.daysByYearMonthWeek[prev.getFullYear()][prev.getMonth()][week].offsetEnd = prev.getDay() ? 7 - prev.getDay() : undefined
                    week = 1
                } else if (date.getDay() === 1) week++

                if (!this.daysByYearMonthWeek[date.getFullYear()]) this.daysByYearMonthWeek[date.getFullYear()] = {}
                if (!this.daysByYearMonthWeek[date.getFullYear()][date.getMonth()]) this.daysByYearMonthWeek[date.getFullYear()][date.getMonth()] = {}
                if (!this.daysByYearMonthWeek[date.getFullYear()][date.getMonth()][week]) this.daysByYearMonthWeek[date.getFullYear()][date.getMonth()][week] = {
                    offsetStart: week !== 1 ? undefined : this.modulo(date.getDay() - 1, 7),
                    days: {},
                }

                this.daysByYearMonthWeek[date.getFullYear()][date.getMonth()][week].days[date.getDate()] = {
                    day: date.getDay(),
                    events: this.eventContainer.getGroupEvents(date.getFullYear(), date.getMonth(), date.getDate(),  -1),
                }

                prev = new Date(date)
            }

            this.daysByYearMonthWeek[prev.getFullYear()][prev.getMonth()][week].offsetEnd = prev.getDay() ? 7 - prev.getDay() : undefined
        },
        getWeek(date) {
            let monthStart = new Date(date);
            monthStart.setDate(0);
            let offset = (monthStart.getDay() + 1) % 7 - 1; // -1 is for a week starting on Monday
            return Math.ceil((date.getDate() + offset) / 7);
        },
        scrollToWeek() {
            const today = new Date()
            const key = `${today.getFullYear()}-${today.getMonth()}-${Math.ceil(today.getDate() / 7)}-1`;
            const week = document.getElementById(key)

            if (!week) return

            week.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
            })
    	},
        modulo(x, n) {
            return ((x % n) + n) % n
        },
    },
    watch: {
        events: {
            deep: true,
            handler(newEvents) {
                if (!newEvents.length) return

                this.eventContainer = new EventContainer(newEvents, this.bgEvents)
            },
        },
        bgEvents: {
            deep: true,
            handler(newEvents) {
                if (!newEvents.length) return

                this.eventContainer = new EventContainer(this.events, newEvents)
            },
        },
        from() {
            this.updateDays()
        },
        until() {
            this.updateDays()
        }
    }
}
</script>

<style>
.current-week:hover {
    color: black;
}
</style>
